/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Modal as ChakraModal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  Table,
  Tbody,
  Tr,
  Th as ChakraTh,
  Td as ChakraTd,
  FormControl,
  FormLabel as ChakraFormLabel,
  Input,
  Divider,
  Textarea,
  Tooltip,
  Link,
  ListItem,
  List,
  Heading,
  useToast
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import PreviewModal from '../../../components/previewModal'
import { EntregaDeTrabalhoService } from '../../../services/EntregaDeTrabalho/EntregaDeTrabalhoService'
import { courseFormat, downloadLink, previewLink } from './helper'
import { IEntregaDeTrabalhoComplete, IEntregaDeTrabalhoUpload } from './models'

interface IProps {
  isOpen: boolean
  onClose: () => void
  id?: number
}

const Th = styled(ChakraTh)`
  padding: 6px 0;
`

const Td = styled(ChakraTd)`
  padding: 6px 0;
`

const FormLabel = ({ children }: { children: React.ReactNode }) => (
  <ChakraFormLabel fontSize="sm">{children}</ChakraFormLabel>
)

const defaultFormValues = {
  professorName: '',
  startDate: '',
  endDate: '',
  workload: '',
  contractNumber: '',
  classCode: '',
  grade: '',
  observations: ''
}

export function EntregaDeTrabalhoReviewModal({ isOpen, onClose, id }: IProps) {
  const service = new EntregaDeTrabalhoService()
  const toast = useToast()
  const [workSubmission, setWorkSubmission] = useState<IEntregaDeTrabalhoComplete | null>(null)
  const [form, setForm] = useState(defaultFormValues)
  const [previewModal, setPreviewModal] = useState(false)
  const [previewData, setPreviewData] = useState<{ url: string; fileName: string; fileSize: number }>({
    url: '',
    fileName: '',
    fileSize: 0
  })

  useEffect(() => {
    setForm(defaultFormValues)
    loadWorkSubmission()
  }, [id])

  const loadWorkSubmission = async () => {
    if (!id) {
      return
    }

    setWorkSubmission(await service.get(id))
  }

  const handleGrade = (e: React.FocusEvent<HTMLInputElement>) => {
    let value = parseFloat(e.target.value)
    if (value < 0) {
      value = 0
    } else if (value > 10) {
      value = 10
    }

    const newValue = Math.round(value * 20) / 20

    setForm({ ...form, grade: newValue.toString() })
  }

  const handleOpenPreview = (upload: IEntregaDeTrabalhoUpload) => {
    setPreviewData({
      fileName: upload.original_name,
      url: previewLink(upload),
      fileSize: Number(upload.size)
    })

    setPreviewModal(true)
  }

  const handleSubmit = async () => {
    const allFieldsAreFilled = Object.values(form).every((value) => value.trim() !== '')
    const startDate = new Date(form.startDate)
    const endDate = new Date(form.endDate)

    if (!allFieldsAreFilled) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Preencha todos os campos para submeter a revisão.',
        status: 'warning',
        duration: 5000,
        isClosable: true
      })
      return
    }

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      toast({
        title: 'Datas inválidas',
        description: 'A data de início e término devem ser datas válidas.',
        status: 'warning',
        duration: 5000,
        isClosable: true
      })
      return
    }

    if (startDate.getTime() > endDate.getTime()) {
      toast({
        title: 'Datas inválidas',
        description: 'A data de início deve ser anterior à data de término.',
        status: 'warning',
        duration: 5000,
        isClosable: true
      })
      return
    }

    if (isNaN(Number(form.workload))) {
      toast({
        title: 'Carga horária inválida',
        description: 'A carga horária deve ser um número válido.',
        status: 'warning',
        duration: 5000,
        isClosable: true
      })
      return
    }

    if (isNaN(Number(form.grade))) {
      toast({
        title: 'Nota inválida',
        description: 'A nota deve ser um número válido.',
        status: 'warning',
        duration: 5000,
        isClosable: true
      })
      return
    }

    await service.review(id!, {
      teacher: form.professorName,
      course_load: form.workload,
      start_date: form.startDate,
      end_date: form.endDate,
      contract_number: form.contractNumber,
      class_code: form.classCode,
      observations: form.observations,
      grade: form.grade
    })

    toast({
      title: 'Trabalho revisado',
      description: 'A revisão do trabalho foi submetida',
      status: 'success',
      duration: 5000,
      isClosable: true
    })
    onClose()
  }

  return (
    workSubmission && (
      <>
        <ChakraModal isOpen={isOpen} onClose={onClose} size="4xl" isCentered scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Ficha de Término</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Table variant="unstyled">
                <Tbody>
                  <Tr>
                    <Th w="150px">Nome do Aluno</Th>
                    <Td>{workSubmission.name}</Td>
                    <Th w="100px">CPF</Th>
                    <Td w="175px">{workSubmission.cpf}</Td>
                  </Tr>
                  <Tr>
                    <Th>Curso</Th>
                    <Td>{workSubmission.course}</Td>
                    <Th>Formato</Th>
                    <Td>{courseFormat[workSubmission.format as 'presencial' | 'online' | 'online-ao-vivo']}</Td>
                  </Tr>
                </Tbody>
              </Table>
              <Divider my={4} />
              <div className="row">
                <div className="col-12">
                  <FormControl>
                    <FormLabel>Nome do Professor</FormLabel>
                    <Input
                      size="sm"
                      value={form.professorName}
                      onChange={(e) => setForm({ ...form, professorName: e.target.value })}
                    />
                  </FormControl>
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-6">
                  <FormControl>
                    <FormLabel>Data de Início</FormLabel>
                    <Input
                      size="sm"
                      type="date"
                      value={form.startDate}
                      onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                      max={form.endDate}
                    />
                  </FormControl>
                </div>
                <div className="col-6">
                  <FormControl>
                    <FormLabel>Data de Término</FormLabel>
                    <Input
                      size="sm"
                      type="date"
                      value={form.endDate}
                      onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                      min={form.startDate}
                    />
                  </FormControl>
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-3">
                  <FormControl>
                    <FormLabel>Carga Horária (horas)</FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      value={form.workload}
                      onChange={(e) => setForm({ ...form, workload: e.target.value })}
                    />
                  </FormControl>
                </div>
                <div className="col-3">
                  <FormControl>
                    <FormLabel>Nº do Contrato</FormLabel>
                    <Input
                      size="sm"
                      value={form.contractNumber}
                      onChange={(e) => setForm({ ...form, contractNumber: e.target.value })}
                    />
                  </FormControl>
                </div>
                <div className="col-3">
                  <FormControl>
                    <FormLabel>Cód. Turma</FormLabel>
                    <Input
                      size="sm"
                      value={form.classCode}
                      onChange={(e) => setForm({ ...form, classCode: e.target.value })}
                    />
                  </FormControl>
                </div>
                <div className="col-3">
                  <FormControl>
                    <FormLabel>Nota</FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      step="0.05"
                      value={form.grade}
                      onChange={(e) => setForm({ ...form, grade: e.target.value })}
                      onBlur={handleGrade}
                    />
                  </FormControl>
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-12">
                  <FormControl>
                    <FormLabel>Observações</FormLabel>
                    <Textarea
                      size="sm"
                      value={form.observations}
                      onChange={(e) => setForm({ ...form, observations: e.target.value })}
                    />
                  </FormControl>
                </div>
              </div>
              <div className="row">
                <div className="col-8 offset-2">
                  <Heading size="sm" mt={4} mb={2}>
                    Arquivos enviados
                  </Heading>
                  <List>
                    {workSubmission.uploads.map((upload) => (
                      <ListItem key={upload.id}>
                        <div className="row mb-2">
                          <div className="col-10">
                            {upload.original_name}{' '}
                            <small className="text-muted">({(Number(upload.size) / 1024 / 1024).toFixed(2)} MB)</small>
                          </div>
                          <div className="col-1">
                            <Tooltip label="Abrir preview">
                              <Link color={'blue.500'} onClick={() => handleOpenPreview(upload)}>
                                <i className="lni lni-eye"></i>
                              </Link>
                            </Tooltip>
                          </div>
                          <div className="col-1">
                            <Tooltip label="Fazer download">
                              <Link target="_blank" href={downloadLink(upload)} rel="noreferrer" color={'blue.500'}>
                                <i className="lni lni-download"></i>
                              </Link>
                            </Tooltip>
                          </div>
                        </div>
                      </ListItem>
                    ))}
                  </List>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" type="button" onClick={onClose}>
                Fechar
              </Button>
              <Button colorScheme="magenta" type="button" ml={4} onClick={handleSubmit}>
                Validar Ficha
              </Button>
            </ModalFooter>
          </ModalContent>
        </ChakraModal>

        <PreviewModal
          isOpen={previewModal}
          onClose={() => {
            setPreviewModal(false)
          }}
          url={previewData.url}
          fileName={previewData.fileName}
          fileSize={previewData.fileSize}
        />
      </>
    )
  )
}
